import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import ToolTip from "components/design/toolTip";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { useCreateSeasonSummaryMutation } from "store/apis/onboarding";
import { useChangeStepMutation } from "store/apis/productOnboarding";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import { setPaymentTitle, setScheduledPayment } from "store/slices/credit";
import { RootState } from "store/store";
import {
  OnboardingProduct,
  Step,
  StepComponent,
} from "types/Models/onboarding";
import { Subscription } from "types/Models/subscription";
import { BackendError } from "types/utils/error";
import { ENTITY_DETAILS_STEP } from "constants/onBoarding";
import * as PRODUCT_ONBOARDING from "constants/productOnboardings";
import { BOOKS_PLUS_PLAN } from "constants/addons";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { CONNECT_BANK } from "constants/fieldTypes";
import { SalesTaxOnboardingSubmit } from "components/SalesTaxOnboardingSubmit/SalesTaxOnboardingSubmit";
import { PRODUCT_ONBOARDING_STEP_KEYS } from "constants/productOnboardingStepKeys";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";

type OnboardingCustomerProps = {
  currentStep: Step | undefined;
  stepSubscription?: Subscription | undefined;
  setSubscriptionModal: (newState: React.SetStateAction<boolean>) => void;
  termAndConditionChecked: boolean;
  setTermAndConditionChecked: (newState: React.SetStateAction<boolean>) => void;
  clientConfirmation: StepComponent | undefined;
  showOnboardingComplete: boolean;
  setShowOnboardingComplete: (newState: React.SetStateAction<boolean>) => void;
  completeOnboarding: (
    groupId: string,
    productId: string,
    closeModal: boolean
  ) => Promise<void>;
  currentProduct: OnboardingProduct;
  setCurrentStep: (step: Step) => void;
  disabled: boolean;
  onClose: () => void;
  selectedEntity?: string | null;
  booksMode?: string;
};

const BtnWrapperComponent = ({
  useTooltip,
  children,
  btnTooltipText,
}: {
  useTooltip: boolean;
  children: ReactNode;
  btnTooltipText: string;
}) => {
  if (useTooltip) {
    return (
      <ToolTip text={btnTooltipText}>
        <div>{children}</div>
      </ToolTip>
    );
  } else {
    return <div>{children}</div>;
  }
};

const NextBtn = ({
  btnTooltipText,
  disableNext,
  currentStep,
  isBooksOnboarding,
  changeStep,
  onClose,
  isLastStep,
}: {
  btnTooltipText: string;
  disableNext: boolean;
  currentStep: Step | undefined;
  isBooksOnboarding: boolean;
  changeStep: (step: "PREV_STEP" | "NEXT_STEP") => void;
  onClose: () => void;
  isLastStep: boolean;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityIdOnSelect = useAppSelector(
    (state) => state.onboardingWidget.entityId
  );
  const entityId = useCurrentEntityId() || entityIdOnSelect!;
  const nextButtonText = isLastStep ? "Complete onboarding" : "Next";
  const isCurrentStepCompleted = currentStep?.status === "COMPLETED";
  const { alertToast } = useToast();

  const { bookkeepingSoftware } = useSelector(
    (state: RootState) => state.booksOnboarding
  );

  const [createSeasonSummary, { isLoading }] = useCreateSeasonSummaryMutation();

  switch (currentStep?.step_key) {
    case "share_software_access":
      const onNext = async () => {
        try {
          const payload = { bookkeeping_software: bookkeepingSoftware };
          await createSeasonSummary({ groupId, entityId, payload }).unwrap();
          changeStep("NEXT_STEP");
        } catch (error) {
          alertToast({
            message: (error as BackendError)?.data?.error?.message,
          });
        }
      };

      return (
        <div>
          <Button customType="primary" onClick={onNext} disabled={disableNext}>
            Next
          </Button>
        </div>
      );

    case "sales_tax_business_details":
      return <SalesTaxOnboardingSubmit changeStep={changeStep} />;
    case "sales_tax_business_owner_details":
      return <SalesTaxOnboardingSubmit changeStep={changeStep} showComplete />;

    case "bookkeeping_plan":
    case "choose_bookkeeping_plan":
      return (
        <Button
          customType="primary"
          onClick={() => changeStep("NEXT_STEP")}
          disabled={disableNext}
        >
          {nextButtonText}
        </Button>
      );

    case "inkle_subscription_complete":
      return (
        <Button customType="primary" onClick={onClose}>
          Complete
        </Button>
      );

    case "enter_information_complete":
      return (
        <Button customType="primary" onClick={onClose}>
          Complete
        </Button>
      );

    default:
      return (
        <BtnWrapperComponent
          useTooltip={isBooksOnboarding && disableNext}
          btnTooltipText={btnTooltipText}
        >
          <Button
            customType="primary"
            onClick={() => changeStep("NEXT_STEP")}
            disabled={disableNext || (isCurrentStepCompleted && isLastStep)}
          >
            {nextButtonText}
          </Button>
        </BtnWrapperComponent>
      );
  }
};

export const OnboardingCustomFooter = ({
  currentStep,
  stepSubscription,
  setSubscriptionModal,
  termAndConditionChecked,
  setTermAndConditionChecked,
  clientConfirmation,
  showOnboardingComplete,
  setShowOnboardingComplete,
  completeOnboarding,
  currentProduct,
  setCurrentStep,
  disabled,
  onClose,
  selectedEntity,
  booksMode,
}: OnboardingCustomerProps) => {
  const { alertToast } = useToast();
  const dispatch = useDispatch();
  const history = useHistory();
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const entityIdOnSelect = useAppSelector(
    (state) => state.onboardingWidget.entityId
  );
  const entityId = useCurrentEntityId() || entityIdOnSelect!;
  const [changeFormStep] = useChangeStepMutation();
  const usEntities = entities.filter((e) => e.country_code === "US");
  const currentUsEntityIndex = usEntities.findIndex(
    (entity) => entity.uuid === selectedEntity
  );
  const nextUsEntity = usEntities[currentUsEntityIndex + 1]?.uuid;
  const prevUsEntity = usEntities[currentUsEntityIndex - 1]?.uuid;

  const { data: ledger } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );

  const { accounts = [] } = ledger || {};

  const isGroupAlreadySubscribed = Boolean(
    stepSubscription?.group_subscription
  );

  const isCurrentStepCompleted = currentStep?.status === "COMPLETED";
  const isBooksOnboarding = currentProduct.name === PRODUCT_ONBOARDING.BOOKS;

  const isBooksplanStep =
    isBooksOnboarding && currentStep?.step_key === "choose_bookkeeping_plan";

  const { data: booksPlusPlans = [] } = useGetSubscriptionsQuery(
    { subscription_types: BOOKS_PLUS_PLAN, groupId, entityId },
    { skip: !groupId || !entityId || !isBooksplanStep }
  );

  const isAlreadyOnBooksPlusPlan = booksPlusPlans.some(
    ({ group_subscription = [] }) => group_subscription.length > 0
  );

  const isConnectBankStep =
    currentStep?.type === CONNECT_BANK && isBooksOnboarding;

  const confirmProceedModal = useModal();

  const handleChangeStep = (step: "PREV_STEP" | "NEXT_STEP") => {
    if (step === "NEXT_STEP" && isConnectBankStep) {
      confirmProceedModal.open();
      return;
    }
    changeStep(step);
  };
  const isTaxOnboarding =
    currentProduct.name === PRODUCT_ONBOARDING.TAX ||
    currentProduct.name === PRODUCT_ONBOARDING.TAXV2;

  const isBooksStepCompleted = isCurrentStepCompleted && isBooksOnboarding;
  const isTermsAndConditionAccepted =
    clientConfirmation || currentStep?.type === CONNECT_BANK
      ? termAndConditionChecked
      : true;

  const hasMandatoryUnfilledField =
    isBooksOnboarding &&
    currentStep?.type === "DEFINED" &&
    Boolean(
      currentStep?.field_values?.some(
        (field) => field.is_mandatory && !field.value
      )
    );

  const isBookkeepingPreferencesStep =
    currentStep?.step_key === "bookkeeping_preferences";

  let disableNext = isBooksStepCompleted
    ? false
    : (isTermsAndConditionAccepted !== undefined &&
        !isTermsAndConditionAccepted) ||
      (isBooksOnboarding &&
        currentStep?.type === CONNECT_BANK &&
        accounts?.length === 0) ||
      hasMandatoryUnfilledField ||
      (isBookkeepingPreferencesStep && !booksMode);

  let isScheduledPayment =
    currentStep?.field_values?.find(
      ({ field_key }) => field_key === "current_year_incorporated"
    )?.value === false ||
    currentStep?.field_values?.find(
      ({ field_key }) => field_key === "current_year_incorporated_v2"
    )?.value;

  const sortedSteps = [...currentProduct.steps].sort(
    (a, b) => a.order - b.order
  );
  const currentStepSortedIndex = Math.max(
    sortedSteps.findIndex((s) => s.uuid === currentStep?.uuid),
    0
  );
  const isLastStep = currentStepSortedIndex >= sortedSteps.length - 1;

  const btnTooltipText = "Please complete all actions before proceeding.";

  const { link } = useConstructInternalLink();

  const changeStep = async (step: "PREV_STEP" | "NEXT_STEP") => {
    // handle entity change in entity details step
    if (
      currentStep?.type === ENTITY_DETAILS_STEP &&
      currentProduct.name === PRODUCT_ONBOARDING.TAXV2
    ) {
      if (step === "PREV_STEP" && prevUsEntity) {
        updateCurrentEntity(prevUsEntity);
        return;
      }
      if (step === "NEXT_STEP" && nextUsEntity) {
        updateCurrentEntity(nextUsEntity);
        return;
      }
    }

    if (
      isBooksOnboarding &&
      currentStep?.step_key === "choose_bookkeeping_plan" &&
      step === "NEXT_STEP" &&
      !isLastStep
    ) {
      const nextStepIndex = currentStepSortedIndex + 1;
      const targetStep = sortedSteps[nextStepIndex];

      const newStep = await changeFormStep({
        groupId: groupId || "",
        formStepId: currentStep?.uuid,
        action: "NEXT_STEP",
        payload: {
          new_form_step_id: targetStep?.uuid || "",
        },
      }).unwrap();

      setCurrentStep(newStep);
      return;
    }

    if (isLastStep && step === "NEXT_STEP") {
      if (isBooksOnboarding) {
        await completeOnboarding(groupId, currentProduct.uuid, false);
        history.push(link("/books"));
        return;
      }

      let closeModal = false;
      if (
        showOnboardingComplete ||
        isTaxOnboarding ||
        currentStep?.step_key === "share_bank_access" ||
        currentStep?.step_key === "schedule_call_with_ops" ||
        currentStep?.step_key === "choose_bookkeeping_plan" ||
        currentStep?.step_key === "us_entity_details_tax_v2" ||
        currentStep?.step_key ===
          PRODUCT_ONBOARDING_STEP_KEYS.SALES_TAX_BUSINESS_OWNER_DETAILS
      ) {
        closeModal = true;
      }

      await completeOnboarding(groupId, currentProduct.uuid, closeModal);

      if (!showOnboardingComplete) {
        setShowOnboardingComplete(true);
      }
      setTermAndConditionChecked(false);
      return;
    }

    const nextStepIndex =
      step === "PREV_STEP"
        ? currentStepSortedIndex - 1
        : currentStepSortedIndex + 1;

    const targetStep = sortedSteps[nextStepIndex];

    if (step === "PREV_STEP" && targetStep?.status === "COMPLETED") {
      setCurrentStep(targetStep);
      return;
    }

    if (
      step === "NEXT_STEP" &&
      currentStep?.status === "COMPLETED" &&
      targetStep
    ) {
      setCurrentStep(targetStep);
      return;
    }

    const newStep = await changeFormStep({
      groupId: groupId || "",
      formStepId: currentStep?.uuid,
      action: step,
      payload: {
        new_form_step_id: targetStep?.uuid || "",
      },
    }).unwrap();

    setCurrentStep(newStep);
  };

  const updateCurrentEntity = async (entityId: string) => {
    try {
      const newStep = await changeFormStep({
        groupId: groupId || "",
        formStepId: currentStep?.uuid,
        action: "SAVE_ENTITY_DATA",
        payload: {
          entity_id: entityId,
        },
      }).unwrap();
      setCurrentStep(newStep);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const amountPayable = isBooksOnboarding
    ? currentStep?.field_values.reduce((acc, cur) => {
        if (cur.value?.amount) {
          acc = Number(cur.value?.amount) + acc;
        }
        return acc;
      }, 0)
    : currentStep?.subscription?.amount;

  const onStartSubscription = () => {
    if (stepSubscription?.subscription_name) {
      setSubscriptionModal(true);
      dispatch(setPaymentTitle(stepSubscription?.subscription_name));
      if (isScheduledPayment) {
        dispatch(setScheduledPayment(true));
      }
    }
  };
  const getTaxOnboardingPlan = useAppSelector(
    (state) => state.onboardingPlans.taxOnboardingPlan
  );

  const isTaxOnboardingPlanPay =
    getTaxOnboardingPlan !== "Free" && !isGroupAlreadySubscribed;

  return (
    <>
      <div className="t-flex t-justify-end t-gap-3">
        <Button
          customType="secondary"
          onClick={() => changeStep("PREV_STEP")}
          disabled={
            currentStep?.order === 0 ||
            currentProduct.status === "COMPLETED" ||
            !Boolean(currentStep?.prev_step_id)
          }
        >
          Back
        </Button>
        {isTaxOnboarding ? (
          <div className="t-flex t-gap-3">
            {isTaxOnboardingPlanPay && currentStep?.subscription?.amount ? (
              <Button customType="primary" onClick={onStartSubscription}>
                {isScheduledPayment
                  ? "Schedule payment"
                  : `Pay ${amountPayable && `$${amountPayable}`}`}
              </Button>
            ) : (
              <NextBtn
                disableNext={disableNext}
                btnTooltipText={btnTooltipText}
                changeStep={handleChangeStep}
                isBooksOnboarding={isBooksOnboarding}
                currentStep={currentStep}
                onClose={onClose}
                isLastStep={isLastStep}
              />
            )}
          </div>
        ) : (
          <div className="t-flex t-gap-3">
            {Number(amountPayable) &&
            !isGroupAlreadySubscribed &&
            !isAlreadyOnBooksPlusPlan ? (
              <Button customType="primary" onClick={onStartSubscription}>
                {isScheduledPayment
                  ? "Schedule payment"
                  : `Pay ${amountPayable && `$${amountPayable}`}`}
              </Button>
            ) : (
              <NextBtn
                disableNext={disableNext}
                btnTooltipText={btnTooltipText}
                changeStep={handleChangeStep}
                isBooksOnboarding={isBooksOnboarding}
                currentStep={currentStep}
                onClose={onClose}
                isLastStep={isLastStep}
              />
            )}
          </div>
        )}
      </div>

      <Modal.Root
        open={confirmProceedModal.isOpen}
        onOpenChange={confirmProceedModal.close}
      >
        <Modal.Content size="small">
          <StatusModal type="warning" title="Ready to proceed?">
            <p className="t-m-0 t-text-body-sm t-text-text-30">
              You won't be able to connect more accounts during onboarding after
              this step, but you can always add them later from Data Sources.
            </p>
          </StatusModal>
          <Modal.Footer className="t-flex t-items-center t-justify-end t-gap-3">
            <Button onClick={confirmProceedModal.close}>Cancel</Button>
            <Button
              customType="primary"
              onClick={() => {
                confirmProceedModal.close();
                changeStep("NEXT_STEP");
              }}
            >
              Yes, Proceed
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
